import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { motion } from 'framer-motion';
import { GET_CATEGORY_ADS } from '../graphql/queries';
import Layout from '../components/Layout';
import AdCard from '../components/Ads/AdCard';
import { ArrowDownIcon } from '@heroicons/react/24/outline';
import { Card, Row, Col } from 'react-bootstrap';

const Category: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const limit = 12;
  const [ads, setAds] = useState<any[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);

  const { loading, error, fetchMore } = useQuery(GET_CATEGORY_ADS, {
    variables: { categorySlug: slug, limit, offset: 0 },
    skip: !slug,
    onCompleted: (resultData) => {
      if (resultData?.categoryAds) {
        setAds(resultData.categoryAds);
        setHasMore(resultData.categoryAds.length === limit);
      }
    },
  });

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !slug) return;

    setLoadingMore(true);
    try {
      const result = await fetchMore({
        variables: { categorySlug: slug, limit, offset: offset + limit },
      });

      if (result.data?.categoryAds) {
        const newAds = result.data.categoryAds;
        setAds((prev) => [...prev, ...newAds]);
        setOffset((prev) => prev + limit);
        setHasMore(newAds.length === limit);
      }
    } catch (err) {
      console.error('Error loading more ads:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, slug, offset, limit, fetchMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loadingMore, loadMore]);

  // Reset when slug changes
  useEffect(() => {
    setAds([]);
    setOffset(0);
    setHasMore(true);
  }, [slug]);

  if (loading && ads.length === 0) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '16rem' }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              border: '3px solid var(--pw-border)',
              borderTopColor: 'var(--pw-primary)',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </Layout>
    );
  }

  if (error && ads.length === 0) {
    return (
      <Layout>
        <div className="text-center py-5">
          <h2 className="fw-bold mb-2" style={{ color: 'var(--pw-text)' }}>Error loading category</h2>
          <p style={{ color: 'var(--pw-text-muted)' }}>Please try again later.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-4">
        <h1 className="fw-bold text-capitalize" style={{ color: 'var(--pw-text)' }}>
          {slug?.replace(/-/g, ' ')}
        </h1>
        <p className="mt-2" style={{ color: 'var(--pw-text-muted)' }}>
          {ads.length} ads found
        </p>
      </div>

      {ads.length === 0 ? (
        <Card
          className="text-center py-5 border-0"
          style={{
            background: 'var(--pw-surface)',
            borderRadius: 0,
            boxShadow: '0 4px 18px rgba(116, 67, 35, 0.05)',
          }}
        >
          <Card.Body>
            <h2 className="h5 fw-semibold mb-2" style={{ color: 'var(--pw-text)' }}>No ads found</h2>
            <p style={{ color: 'var(--pw-text-muted)' }}>There are no ads in this category yet.</p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row className="g-3 g-md-4">
            {ads.map((ad: any, index: number) => (
              <Col key={`${ad.id}-${index}`} xs={6} md={4} lg={4} xl={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.03, 0.3), duration: 0.4 }}
                >
                  <AdCard ad={ad} />
                </motion.div>
              </Col>
            ))}
          </Row>

          {/* Load More Button */}
          {!loadingMore && hasMore && (
            <div className="d-flex justify-content-center pt-5">
              <button
                onClick={loadMore}
                className="d-flex align-items-center gap-2 border-0 fw-semibold"
                style={{
                  padding: '14px 28px',
                  borderRadius: 14,
                  background: 'var(--pw-surface)',
                  borderWidth: '1.5px',
                  borderStyle: 'solid',
                  borderColor: 'var(--pw-border)',
                  color: 'var(--pw-text)',
                  fontSize: 15,
                  boxShadow: '0 4px 14px rgba(116, 67, 35, 0.06)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--pw-bg-soft)';
                  e.currentTarget.style.borderColor = '#f2b58f';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 22px rgba(116, 67, 35, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--pw-surface)';
                  e.currentTarget.style.borderColor = 'var(--pw-border)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(116, 67, 35, 0.06)';
                }}
              >
                <ArrowDownIcon style={{ width: 18, height: 18 }} />
                Load More
              </button>
            </div>
          )}

          {/* Loading Spinner */}
          {loadingMore && (
            <div className="d-flex justify-content-center py-5">
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  border: '3px solid var(--pw-border)',
                  borderTopColor: 'var(--pw-primary)',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* End of List */}
          {!hasMore && ads.length > 0 && (
            <div className="text-center py-5">
              <p className="mb-0" style={{ fontSize: 15, color: 'var(--pw-text-muted)' }}>
                You&apos;ve seen all the ads
              </p>
            </div>
          )}

          {/* Observer Target */}
          {hasMore && (
            <div ref={observerTarget} style={{ height: 40 }} />
          )}
        </>
      )}
    </Layout>
  );
};

export default Category;
